import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { IoChevronDown, IoChevronBack, IoChevronForward } from "react-icons/io5";

const HEADER_HEIGHT = 0; // Set đúng chiều cao header fixed nếu có (ví dụ: 64)

const DesktopNavMenu = ({ menuItems = [] }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const timeoutRef = useRef();

  // Scroll ref & item ref
  const scrollRef = useRef();
  const itemRefs = useRef({});

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 5);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
  };

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    const activeRef = itemRefs.current[currentPath];
    if (el && activeRef && activeRef.current) {
      const containerRect = el.getBoundingClientRect();
      const itemRect = activeRef.current.getBoundingClientRect();
      const offset =
        itemRect.left -
        containerRect.left -
        el.clientWidth / 2 +
        itemRect.width / 2;
      el.scrollBy({ left: offset, behavior: "smooth" });
    }
    // eslint-disable-next-line
  }, [currentPath, menuItems]);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, []);

  const scrollBy = (offset) => {
    scrollRef.current?.scrollBy({ left: offset, behavior: "smooth" });
  };

  // Dropdown hover logic
  const handleMouseEnter = (label) => {
    clearTimeout(timeoutRef.current);
    setDropdownOpen(label);
  };
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setDropdownOpen(null);
    }, 180);
  };

  // Tính vị trí submenu dropdown (chuẩn mọi trình duyệt)
  const getDropdownPosition = (itemKey) => {
    const itemEl = itemRefs.current[itemKey]?.current;
    if (!itemEl) return { left: 0, top: HEADER_HEIGHT };

    const itemRect = itemEl.getBoundingClientRect();
    const DROPDOWN_MIN_WIDTH = 310; // hoặc 224 = 14rem, tuỳ style
    let left = itemRect.left;

    // Không cho dropdown tràn ra ngoài phải/trái màn hình
    if (left + DROPDOWN_MIN_WIDTH > window.innerWidth - 8) {
      left = window.innerWidth - DROPDOWN_MIN_WIDTH - 8;
    }
    if (left < 8) left = 8;

    let top = itemRect.bottom + 4; // + HEADER_HEIGHT nếu cần
    if (top < HEADER_HEIGHT + 8) top = HEADER_HEIGHT + 8;

    return { left, top };
  };

  // Render dropdown submenu bằng Portal
  const renderDropdown = (item) => {
  if (!dropdownOpen || dropdownOpen !== item.label) return null;
  const { left, top } = getDropdownPosition(item.label);
  return createPortal(
    <div
      className="fixed"
      style={{
        left,
        top: top - 4, // Kéo submenu lên sát hơn, có thể test top: top - 2 hoặc top nếu cần
        minWidth: "14rem",
        zIndex: 9999,
      }}
      onMouseEnter={() => handleMouseEnter(item.label)}
      onMouseLeave={handleMouseLeave}
    >
      <ul
        className="
          bg-bg border border-border shadow-xl
          rounded-b-2xl rounded-t-none
          animate-fadeIn
          pt-0 mt-0
          overflow-hidden
        "
        style={{
          // Xoá border top hoặc cho trùng màu nền menu
          borderTop: "none",
        }}
      >
        {item.subMenuItems.map((sub) => (
          <li key={sub.to}>
            <Link
              to={sub.to}
              className={`
                block px-5 py-2 font-semibold transition relative
                ${currentPath === sub.to
                  ? "text-primary bg-primary/10"
                  : "text-menu hover:text-primary hover:bg-primary/5"}
              `}
            >
              {sub.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>,
    document.body
  );
};


  return (
    <div className="relative flex items-center w-full bg-transparent px-0 md:px-3" style={{ zIndex: 50 }}>
      {/* Nút trái */}
      {canScrollLeft && (
        <button
          type="button"
          className="absolute left-0 h-full flex items-center px-1 z-20 bg-gradient-to-r from-bg via-bg/70 to-transparent"
          aria-label="Scroll left"
          onClick={() => scrollBy(-120)}
          tabIndex={-1}
        >
          <IoChevronBack className="w-6 h-6 text-primary" />
        </button>
      )}

      {/* Scroll ngang phần list */}
      <div
        ref={scrollRef}
        className={`
          w-full overflow-x-auto no-scrollbar scroll-smooth relative z-10
        `}
        style={{ scrollbarWidth: "none" }}
      >
        <ul className="flex flex-row items-center gap-1 relative whitespace-nowrap">
          {menuItems.map((item) =>
            !item.subMenuItems ? (
              <li
                key={item.to}
                ref={
                  (itemRefs.current[item.to] = itemRefs.current[item.to] || React.createRef())
                }
              >
                <Link
                  to={item.to}
                  className={`
                    block px-4 py-2 rounded font-semibold transition relative
                    ${currentPath === item.to
                        ? "bg-primary text-white shadow-sm"
                        : "text-menu hover:text-primary hover:bg-primary/10"}
                  `}
                >
                  {item.label}
                </Link>
              </li>
            ) : (
              <li
                key={item.label}
                ref={
                  (itemRefs.current[item.label] = itemRefs.current[item.label] || React.createRef())
                }
                className="relative"
                style={{ zIndex: 60 }}
                onMouseEnter={() => handleMouseEnter(item.label)}
                onMouseLeave={handleMouseLeave}
              >
                <div
                  className={`
                    flex items-center gap-1 px-4 py-2 rounded font-semibold cursor-pointer select-none transition
                    ${dropdownOpen === item.label
                      ? "bg-primary text-white shadow"
                      : "text-menu hover:text-primary hover:bg-primary/10"
                    }
                  `}
                >
                  {item.label} <IoChevronDown className="w-4 h-4" />
                </div>
                {renderDropdown(item)}
              </li>
            )
          )}
        </ul>
      </div>

      {/* Nút phải */}
      {canScrollRight && (
        <button
          type="button"
          className="absolute right-0 h-full flex items-center px-1 z-20 bg-gradient-to-l from-bg via-bg/70 to-transparent"
          aria-label="Scroll right"
          onClick={() => scrollBy(120)}
          tabIndex={-1}
        >
          <IoChevronForward className="w-6 h-6 text-primary" />
        </button>
      )}
    </div>
  );
};

export default DesktopNavMenu;
